import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  QueryList,
  Type,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { ModalService } from '@base/services/private/modal.service';
import { Attendee } from '@base/models/responses/attendee.res';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { ModalRequestMeetComponent } from '@base/components/modal-request-meet/modal-request-meet.component';
import { ModalConfig } from '@base/models/others/modal-config';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { ChatComponent } from '@navbar/chat/chat.component';
import { GlobalService } from '@base/services/private/global.service';
import { extractDynamicImport } from '@base/utils';
import { WhiteLabel } from '@config/whitelabel-keys.constant';

@Component({
  selector: 'app-modal-people-list',
  imports: [ModalHeaderComponent, ModalBodyComponent, forwardRef(() => TranslatePipe), NgxSkeletonLoaderComponent],
  templateUrl: './modal-people-list.component.html',
  styleUrl: './modal-people-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalPeopleListComponent implements AfterViewInit, OnDestroy {
  @Input() modalId = '';
  @Input() isCloseAll = false;
  @Input() attendees: Attendee[] = [];
  @ViewChildren('cardAttendees', { read: ViewContainerRef }) cardAttendees!: QueryList<ViewContainerRef>;
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly modalSrv = inject(ModalService);
  private readonly globalSrv = inject(GlobalService);

  modalRequestMeet: Type<unknown> | undefined;

  ngAfterViewInit(): void {
    this.renderAttendes();
    (async () => {
      await this.loadComponents();
    })();
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Asynchronously loads components for the explore page.
   *
   * @returns Promise<void> Resolves when all components are successfully loaded.
   */
  async loadComponents(): Promise<void> {
    try {
      const components = await Promise.allSettled([
        this.dynamicImportLoaderSrv.loadComponent(this.viewContainerRef, WhiteLabel.COMPONENTS.ModalRequestMeet, false),
      ]);

      if (components) {
        const [modalRequestMeet] = components;

        this.modalRequestMeet = extractDynamicImport(modalRequestMeet)?.component || undefined;
      } else {
        throw new Error('Components could not be loaded.');
      }
    } catch (error) {
      console.error('Failed to load components:', error);
    }
  }

  /**
   * Opens the Request Meet modal for a specific attendee.
   * @param item - The attendee data to pass into the modal.
   * @returns void
   */
  showModalRequestMeet(item: Attendee): void {
    this.close(); // close current modal

    // if any chat id exists, open chat modal instead
    if (item?.chat_id) {
      this.modalSrv.closeAll([]); // close all modal include chat modal
      this.showModalChat(item);
      return;
    }

    // otherwise, open request meet modal
    const data: Partial<ModalRequestMeetComponent> = {
      peopleId: item?.id_attendee,
      isCloseAll: this.isCloseAll,
    };
    const config: ModalConfig = {
      backdrop: 'static',
      keyboard: false,
    };
    this.modalSrv.showModalRequestMeet({
      addsData: data,
      addsConfig: config,
      component: this.modalRequestMeet,
    });
  }

  /**
   * Opens the Chat modal for a specific attendee.
   * @param item  - The attendee data to pass into the modal.
   * @returns void
   */
  showModalChat(item: Attendee): void {
    const data: Partial<ChatComponent> = {
      peopleId: item?.id_attendee,
      chatroomId: item?.chat_id,
    };
    this.modalSrv.showModalChat({ addsData: data });
  }

  /**
   * Closes the current modal by its ID.
   * @returns void
   */
  close(): void {
    this.modalSrv.close();
  }

  /**
   * Dynamically renders attendee cards inside the modal.
   * Iterates through attendees and loads their components.
   * @returns void
   */
  renderAttendes() {
    this.attendees.forEach(async (item, index: number) => {
      const component = await this.dynamicImportLoaderSrv.loadCardAttendee(this.viewContainerRef);
      if (component) {
        component.setInput('img', {
          url: item.attendee_image,
          alt: item.name,
        });
        component.setInput('user', {
          name: item.name,
          title: item.title,
          attendeeActive: item.attendee_active,
          typeOfReq: item.type_of_req,
        });

        if (this.cardAttendees) {
          this.cardAttendees.get(index)?.clear();
          this.cardAttendees.get(index)?.insert(component.hostView);
        }
      }
    });
    this.changeDetectorRef.detectChanges();
    this.changeDetectorRef.markForCheck();
  }
}
